import { CalendarFilter, EncounterStatus, EncounterStatusV2, Gender, Utils, ViewMode } from "../services/utils";
import { Patient } from "./patient.model";
import { EncounterSchedule, Procedure } from "./procedure.model";

export class Appointment {
  id?: string;
  /** Public encounter id saved on the encounter: ENC + 6 alphanumeric + year. */
  displayNumber?: string | null;
  patientId?: string;
  encounterType: string;
  organizationId?: string;
  encounterDateTime?: string;
  providerId: string;
  providerNpi?: string;
  resourceId?: string;
  scheduleResourceId?: string;
  resourceName?: string;
  /** When list-clinic / encounter API returns NPI for the scheduled resource. */
  scheduleResourceNpi?: string;
  providerName: string;
  providerTitle?: string;
  locationId?: string;
  visitType?: string;
  /** e.g. online, walking_in, or provider id — shown on dashboard schedule list */
  referralSource?: string;
  /**
   * Referral source facet value (often UUID/slug) from filters / directory API.
   * {@link referralSource} may be a display label only — client facet matching prefers this id when set.
   */
  referralSourceId?: string;
  /** When present (e.g. list-clinic / Engage), used to match referral source from Engage by-patient-ids */
  referralId?: string;
  reasonForVisit?: string;
  /** List-clinic / Engage worklist: reason of visit (API `reasonOfVisit`). */
  reasonOfVisit?: string | null;
  /** List-clinic / Engage worklist: account service label (API `service`). */
  service?: string | null;
  /** Worklist Services column: most recent encounter service (API `last_service`). */
  lastService?: string | null;
  diagnoses?: string;
  procedures?: string;
  driveTime?: number;
  admissionDate?: string;
  dischargeDate?: string;
  /** From encounter_status.clinicalStatus when API sends it (preferred for display). */
  clinicalStatus?: string;
  /** From encounter_status.billingStatus — persisted Ready for Billing when computed. */
  billingStatus?: string | null;
  /** From encounter_status.signReqStatus — persisted Sign Req when computed. */
  signReqStatus?: string | null;
  encounterStatus?: string;
  /** From list-clinic API: e.g. "New Patient" | "Established" */
  newOrEstablishedStatus?: string;
  /** When the encounter was first marked Established (ISO timestamp). */
  establishedAt?: string;
  scheduleStatus?: string;
  dischargeDisposition?: string;
  encounterSummary?: string;
  paymentSource?: string;
  encounterPriority?: string;
  attendingPhysician?: string;
  referringPhysician?: string;
  encounterDuration?: number;
  encounterClass?: string;
  encounterRiskLevel?: string;
  relatedDocuments?: string;
  followUpInstructions?: string;
  billingCode?: string;
  consentObtained?: boolean;
  encounterComments?: string;
  encountersPatient?: {
    name: string;
    id: string;
    dob: string;
    gender: string;
    age: number;
    phone?: string;
    /** From list-clinic when referral id is nested on patient, not encounter root */
    referralId?: string;
  };
  encounterSchedule?: EncounterSchedule;
  encounterProcedure: Procedure;
  isConfirmed: boolean;
  actualCheckIn?: string;
  isSelected?: boolean;
  /** Optional list-clinic / Engage fields for worksheet grid */
  createdAt?: string;
  updatedAt?: string;
  coordinatorName?: string;
  /** PHI directory / Engage workflow UUID (`workFlowId`). */
  workFlowId?: string;
  /** PHI directory / Engage workflow display name (`workFlowName`). */
  workFlowName?: string;
  /** Queue position / label when API provides it */
  queue?: string | number;
  /** Referral pipeline / workflow status when API provides it */
  referralWorkflowStatus?: string;
  /**
   * Worklist Queue column (Engage): account queue row id (`que_id` from referral / PATCH target).
   * Kept separate from {@link referralWorkflowStatus} (patient / lead pipeline).
   */
  referralQueueId?: string;
  /** Worklist Queue column: display name for the queue (from Engage or resolved from account queue list). */
  referralQueueLabel?: string;
  /**
   * Worklist action-column badges (set from Engage referral merge and/or list-clinic extras).
   */
  worksheetEngageIconCount?: number;
  worksheetReminderIconCount?: number;
  /** Unread SMS count (timeline `unread_message` filter). */
  worksheetUnreadMessageCount?: number;
  /** Missed / voicemail / form-filled notification count (`notifications` filter). */
  worksheetNotificationsChannelCount?: number;
  /** Open / incomplete checklist items when list-clinic exposes a count. */
  worksheetChecklistOpenCount?: number;
  /** Worklist Fol.Dos: scheduled follow-up encounter datetime (ISO) when referral has a follow-up. */
  followUpEncounterDateTime?: string;
  /** Worklist Recent DOS (API `last_dos`). */
  lastDosDateTime?: string;
  /** Worklist Encounter Status (API `encounterStatus`). */
  lastEncounterStatus?: string;
  /** True when the encounter has services not covered by prior auth. */
  isRetroAuth?: boolean;
  /** PHI `encounter_status.retroAuthStatus` (mirrors isRetroAuth). */
  retroAuthStatus?: boolean;
  /** PHI `encounter_status.authStatus` (prior-auth workflow). */
  authStatus?: string | null;
  /** PHI `encounter_status.authRequirementStatus` (Auth Required / No Auth Required). */
  authRequirementStatus?: string | null;
  /** Procedure list: appointment memo from `list-procedure` API. */
  memo?: string | null;
  /** Procedure list: latest appointment memo from `list-procedure` API. */
  latestAppointmentMemo?: AppointmentMemoSummary | null;
  /** PHI directory `primary_payer_name` (latest encounter biller lookup). */
  primaryPayerName?: string;
  /** PHI directory `eligibility` (`Verified`, `Unverified`, `No Coverage`, …). */
  eligibility?: string | null;
  /** PHI directory `authorizationStatus` display label. */
  authorizationStatus?: string;
  /** PHI directory `claimStatus` (latest encounter). */
  claimStatus?: string;
  /**
   * PHI directory `latestEncounterId` — authoritative for Engage list row click navigation.
   * When explicitly `null`, do not open `/encounters` even if {@link id} was enriched elsewhere.
   */
  latestEncounterId?: string | null;
}

/** Latest appointment memo embedded on procedure list rows. */
export interface AppointmentMemoSummary {
  id?: string;
  encounterId?: string;
  activityType?: string;
  activityBy?: string;
  comment?: string;
  notes?: string;
  createdAt?: string;
}

/**
 * Engage referral id from list-clinic / encounter payloads: root `referralId` or nested
 * `encountersPatient.referralId`.
 */
export function getAppointmentReferralId(
  appointment?: Pick<Appointment, "referralId" | "encountersPatient"> | null
): string {
  const top = (appointment?.referralId || "").trim();
  if (top) {
    return top;
  }
  return (appointment?.encountersPatient?.referralId || "").trim();
}

export function appointmentVisitTypeIsProcedure(
  appointment?: Pick<Appointment, "visitType" | "encounterSchedule"> | null
): boolean {
  const raw = String(
    appointment?.encounterSchedule?.visitType ?? appointment?.visitType ?? ""
  )
    .trim()
    .toLowerCase();
  return raw === "procedure";
}

export function appointmentIsCheckedIn(
  appointment?: Pick<
    Appointment,
    "encounterStatus" | "actualCheckIn" | "encounterSchedule"
  > | null
): boolean {
  if (appointment?.encounterStatus === EncounterStatus.CheckedIn) {
    return true;
  }
  const raw =
    appointment?.actualCheckIn ?? appointment?.encounterSchedule?.actualCheckIn;
  return !!String(raw ?? "").trim();
}

/** Admissions list: disable delete when submitted or procedure visit is checked in. */
export function isAppointmentDeleteDisabled(appointment: Appointment): boolean {
  if (appointment.scheduleStatus === EncounterStatusV2.Submitted) {
    return true;
  }
  return (
    appointmentVisitTypeIsProcedure(appointment) &&
    appointmentIsCheckedIn(appointment)
  );
}

export class NewAppointmentPayload {
  patientId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: Gender | string = "";
  address: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  providerId: string = "";
  resourceId?: string = "";
  locationId?: string = "";
  encounterType?: string = "";
  visitType?: string = "";
  referralSource?: string = "";
  service?: string = "";
}

export class SearchByDobResponse {
  searchStatus?: PatientSearchStatus;
  searchTerm: string;
  resultSet?: Patient[];
  constructor(date: string = null) {
    this.searchStatus = null;
    this.resultSet = [];
    this.searchTerm = date;
  }
}

export class AppointmentCountResponse {
  filter: FilterCount[] = [];
  physicians: AppointmentCount[] = [];
  resources: AppointmentCount[] = [];
  locations: LocationsCount[] = [];
  visitTypes: VisitTypesCount[] = [];
  totalCount?: number;
}

export interface AppointmentCount {
  id: string;
  count: number;
}

export interface FilterCount {
  label: string;
  count: number;
  /** Present on some `referrals/counts` filter buckets (e.g. Booked, No Show, Established). */
  encounters?: UpcomingEncounterRef[];
}

export interface LocationsCount {
  label: string;
  count: number;
}

export interface VisitTypesCount {
  label: string;
  count: number;
}

/** Column facet projection for list-clinic (`facetField` body param). */
export type ScribeListClinicFacetField =
  | "dob"
  | "dos"
  | "name"
  | "service"
  | "resource"
  | "scheduleStatus";

/** soap_note provider signature filter values for list-clinic `signReq`. */
export type ScribeListClinicSignReqValue = "signed" | "unsigned" | "null";

/** Filters for scribe `list-clinic` loads. */
export interface ScribeListClinicFilters {
  providerIds?: string[];
  /** Schedule resource ids (list-clinic `resources`). */
  resourceIds?: string[];
  filterStatus?: string[];
  /**
   * Optional Note Status multi-select → body `scheduleStatus`
   * (same labels as the scheduleStatus facet / filterStatus).
   */
  scheduleStatuses?: string[];
  /**
   * Optional Sign Req multi-select → body `signReq`
   * (`signed` | `unsigned` | `null`).
   */
  signReq?: ScribeListClinicSignReqValue[];
  /** Organization service ids (list-clinic `service`). */
  serviceIds?: string[];
  /** Claim status api values (list-clinic `claimStatus`). */
  claimStatuses?: string[];
  /** yyyy-MM-dd */
  startDate?: string;
  /** yyyy-MM-dd */
  endDate?: string;
  searchQuery?: string;
  sortField?: string;
  sortDirection?: "ASC" | "DESC";
  /** When false, list-clinic rows are not narrowed to the default scribe schedule allowlist. */
  enforceScribeScheduleAllowlist?: boolean;
  /**
   * When set, list-clinic returns distinct values only (`facetValues` string[]).
   * Same filters as a normal list; omit for full encounter rows.
   */
  facetField?: ScribeListClinicFacetField;
  /** Optional multi-select patient DOBs (`yyyy-MM-dd`) → body `dobs`. */
  dobs?: string[];
  /** Optional multi-select DOS days (`yyyy-MM-dd`) → body `dos`. */
  dos?: string[];
  /** Optional multi-select patient names ("First Last") → body `names`. */
  names?: string[];
}

/**
 * list-clinic response when `facetField` is set:
 * `{ "facetValues": ["…"] }`
 * - dob / dos / name / service / resource / scheduleStatus
 */
export interface ListClinicFacetResponse {
  data?: Appointment[];
  facetValues?: Array<string | { value?: string; label?: string; name?: string }>;
  totalRowsCount?: number;
  pageStart?: number;
  pageLength?: number;
  totalPages?: number;
  currentPage?: number;
}

export class AdmissionListPayload {
  providers?: string[] = [];
  resources?: string[] = [];
  filterStatus?: string[] = [];
  /** Prior-auth workflow on encounter_status.authStatus (Pending Auth, Authorized, Denied, No Auth Required). */
  authStatus?: string[] = [];
  /** list-clinic: true = confirmed only, false = unconfirmed only. Omit for all. */
  isConfirmed?: boolean;
  filterLocation?: string[] = [];
  visitType?: string[] = [];
  /** When set, list-clinic may filter by referral source id/value (backend support optional; client filters if not). */
  referralSources?: string[];
  filterDate?: {
    startDate: string;
    endDate: string;
  };
  pageStart?: number = 0;
  pageLength?: number = 15;
  sortField?: string = "encounterDateTime";
  sortDirection?: "ASC" | "DESC" = "DESC";
  searchQuery?: string = "";
  
  constructor(selectedDate: DateRange) {
    this.filterDate = {
      startDate: Utils.getStartOfDayISODate(selectedDate.startDate),
      endDate: Utils.getEndOfDayISODate(selectedDate.endDate),
    };
  }
}

/** POST `/api/v1/procedure/list-procedure` (procedure listing + pipeline). */
export interface ProcedureListProcedurePayload {
  companyId?: string;
  providers?: string[];
  resources?: string[];
  filterStatus?: string[];
  filterLocation?: string[];
  visitType?: string[];
  referralSources?: string[];
  source?: string[];
  /** Account service ids (same shape as scribe list-clinic `service`). */
  service?: string[];
  filterDate?: {
    startDate: string;
    endDate: string;
  };
  pageStart?: number;
  pageLength?: number;
  sortField?: string;
  sortDirection?: "ASC" | "DESC";
  searchQuery?: string;
}

/** POST `/api/v1/procedure/list-procedure/status-counts` — tab badge counts (no pagination/sort/filterStatus). */
export interface ProcedureListStatusCountsPayload {
  companyId?: string;
  providers?: string[];
  resources?: string[];
  filterLocation?: string[];
  visitType?: string[];
  referralSources?: string[];
  source?: string[];
  service?: string[];
  filterDate?: {
    startDate: string;
    endDate: string;
  };
  searchQuery?: string;
}

export interface ProcedureListStatusCountRow {
  filterStatus: string;
  count: number;
}

export interface ProcedureListStatusCountsResponse {
  counts: ProcedureListStatusCountRow[];
}

/** Distinct counts for Schedule / referrals left-panel "All (N)" labels; derived from the same list-clinic response as `data`. */
export interface UniqueFilterDimensions {
  referralSources: number;
  resources: number;
  locations: number;
  visitTypes: number;
}

export interface PaginatedAppointmentResponse {
  data: Appointment[];
  totalRowsCount: number;
  pageStart: number;
  pageLength: number;
  totalPages: number;
  currentPage: number;
  /** Present when returned from getAdmissionsList (computed client-side from `data` / full processed page). */
  uniqueFilterDimensions?: UniqueFilterDimensions;
}

export interface AdmissionDisplayMode {
  view: ViewMode;
  filter: CalendarFilter;
}

export interface UnConfirmedAppointment {
  id: string;
  nameInitials: string;
  fullName: string;
  dos: string;
  prepInfo: string;
  procedureType?: string;
  visitType?: string;
  providerName: string;
  resourceName?: string;
  clinicalStatus?: string;
}

export interface AppointmentChecklist {
  idCard: boolean;
  indications: boolean;
  insuranceCard: boolean;
  labResults: boolean;
  priorAuth: boolean;
  procedureRelatedDiagnosis: boolean;
  schedule: boolean;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export enum PatientSearchStatus {
  Pending = 1,
  Found,
  NotFound,
  Failed,
}

/** Response from GET /api/v1/encounters/upcoming-encounters */
export interface UpcomingEncountersDay {
  dayOfWeek: string;
  dateOrdinal: string;
  dateOfMonth: number;
  encounterCount: number;
}

export interface UpcomingEncounterRef {
  encounterId: string;
  patientId: string;
}

export interface UpcomingEncountersStatBucket {
  count: number;
  encounters: UpcomingEncounterRef[];
}

/** Legacy API: number; current API: {@link UpcomingEncountersStatBucket}. */
export type UpcomingEncountersStatValue = number | UpcomingEncountersStatBucket;

export interface UpcomingEncountersStats {
  clinic: UpcomingEncountersStatValue;
  telehealth: UpcomingEncountersStatValue;
  procedures: UpcomingEncountersStatValue;
  other: UpcomingEncountersStatValue;
}

export interface UpcomingEncountersResponse {
  days: UpcomingEncountersDay[];
  stats: UpcomingEncountersStats;
}

/** Normalize upcoming-encounters `stats.*` (number or `{ count, encounters }`). */
export function normalizeUpcomingEncountersStatBucket(
  value: UpcomingEncountersStatValue | null | undefined
): UpcomingEncountersStatBucket {
  if (value == null) {
    return { count: 0, encounters: [] };
  }
  if (typeof value === "number") {
    return { count: value, encounters: [] };
  }
  return {
    count: value.count ?? 0,
    encounters: Array.isArray(value.encounters) ? value.encounters : [],
  };
}

/** Response from GET /api/v1/encounters/status-counts */
export interface EncounterStatusCountsResponse {
  /** Legacy: number; current API: {@link UpcomingEncountersStatBucket}. */
  Open?: UpcomingEncountersStatValue;
  Incomplete: UpcomingEncountersStatValue;
  "Sign Req": UpcomingEncountersStatValue;
  Unsubmitted: UpcomingEncountersStatValue;
}

/** Alias for status-counts buckets (same shape as upcoming-encounters stats). */
export type EncounterStatusCountBucket = UpcomingEncountersStatBucket;

/** Normalize status-counts fields (number or `{ count, encounters }`). */
export function normalizeEncounterStatusCountBucket(
  value: UpcomingEncountersStatValue | null | undefined
): EncounterStatusCountBucket {
  return normalizeUpcomingEncountersStatBucket(value);
}

/** Schedule snapshot on GET /api/v1/encounters/{id}/follow-up-info */
export interface FollowUpEncounterScheduleSummary {
  encounterId?: string;
  dos?: string;
  location?: string;
  lineLocation?: string;
  visitType?: string;
  scheduleResourceId?: string;
  scheduleResourceName?: string;
  checkIn?: string | null;
  actualCheckIn?: string | null;
  procedureTime?: string | null;
  reasonForVisit?: string;
  scheduleStatus?: string;
  /** Account service id or label from follow-up schedule API. */
  service?: string;
}

/** One scheduled follow-up on the original encounter. */
export interface FollowUpListItem {
  followUpId: string;
  followUpDate?: string;
  carryForward?: boolean;
  followUpStatus?: string;
  followUpProvider?: string;
  followUpEncounter?: FollowUpEncounterScheduleSummary;
  reasonForVisit?: string;
}

/** GET /api/v1/encounters/:encounterId/follow-ups */
export interface EncounterFollowUpsResponse {
  encounterId?: string;
  hasFollowUp?: boolean;
  followUpCount?: number;
  /** Most recent follow-up encounter id */
  followUpId?: string;
  followUps?: FollowUpListItem[];
}

/** Legacy follow-up-info API response (includes schedule details for UI). */
export interface FollowUpInfoResponse extends EncounterFollowUpsResponse {
  followUpDate?: string;
  carryForward?: boolean;
  originalEncounter?: FollowUpEncounterScheduleSummary;
  followUpEncounter?: FollowUpEncounterScheduleSummary;
  scheduleInfo?: {
    original?: FollowUpEncounterScheduleSummary;
    followUp?: FollowUpEncounterScheduleSummary;
  };
}

/** Allowed Engage `GET /api/v1/referrals` notification boolean query keys (worksheet filter sheet). */
export const WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS = [
  "has_new_lead",
  "has_missed",
  "has_sms",
  "has_voicemail",
  "has_reminder",
] as const;

export type WorksheetReferralNotificationFilterKey =
  (typeof WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS)[number];

/** Unit separator for multiple display names in {@link WorksheetGridClientFilters.referralsFullNameFilters}. */
export const WORKSHEET_REFERRALS_FULL_NAME_UNIT_SEP = "\u001f";

export type WorksheetNameOptionForFullName = { value: string; label: string };

/**
 * Builds stored CSV for Engage `GET /api/v1/referrals` `full_name` params from name column keys and option labels.
 */
export function computeReferralsFullNameFiltersCsv(
  keysCsv: string | null | undefined,
  opts: readonly WorksheetNameOptionForFullName[] | null | undefined
): string | null {
  const keys = (keysCsv ?? "").trim();
  if (!keys) {
    return null;
  }
  const map = new Map<string, string>();
  for (const o of opts ?? []) {
    const v = String(o.value ?? "").trim();
    if (v) {
      map.set(v, String(o.label ?? "").trim());
    }
  }
  const labels: string[] = [];
  for (const key of keys
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)) {
    const lab = map.get(key);
    if (lab) {
      labels.push(lab);
    } else if (key.startsWith("n:")) {
      const norm = key.slice(2).trim();
      if (norm) {
        labels.push(norm);
      }
    }
  }
  return labels.length ? labels.join(WORKSHEET_REFERRALS_FULL_NAME_UNIT_SEP) : null;
}

export function parseReferralsFullNameFiltersCsv(csv: string | null | undefined): string[] {
  const s = (csv ?? "").trim();
  if (!s) {
    return [];
  }
  return s
    .split(WORKSHEET_REFERRALS_FULL_NAME_UNIT_SEP)
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Human-readable labels for worklist name filter dashboard tags. */
export function worksheetNameFilterTagLabels(
  grid: Pick<WorksheetGridClientFilters, "namePatientKeysContains" | "referralsFullNameFilters">
): string[] {
  const fromStored = parseReferralsFullNameFiltersCsv(grid.referralsFullNameFilters);
  if (fromStored.length) {
    return fromStored;
  }
  const keys = (grid.namePatientKeysContains ?? "").trim();
  if (!keys) {
    return [];
  }
  return keys
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((key) => {
      if (key.startsWith("n:")) {
        return key.slice(2).trim();
      }
      return "";
    })
    .filter(Boolean);
}

/** Worklist column-aligned text filters (client-side row match, except Name → list-clinic searchQuery). */
export interface WorksheetGridClientFilters {
  /** Sent to list-clinic as searchQuery on Apply; also stored on snapshot for saved views. */
  nameContains: string;
  /** Comma-separated stable row keys (`id:` / `ref:` / `n:`) for the worklist name column filter. */
  namePatientKeysContains: string | null;
  /**
   * Display names for Engage `GET /api/v1/referrals` `full_name` (one param per value); kept in sync with
   * {@link namePatientKeysContains} in list view. Unit-separated; see {@link WORKSHEET_REFERRALS_FULL_NAME_UNIT_SEP}.
   */
  referralsFullNameFilters: string | null;
  /** Substring match on displayed DOB (MM/dd/yyyy). */
  dobContains: string;
  /** Comma-separated local calendar days `yyyy-MM-dd` (worklist DOB column filter, Excel-style). */
  dobDayKeysContains: string | null;
  /** Comma-separated account queue row ids → directory/referrals `que_id` (exact match, one or more). */
  queueContains: string | null;
  /** Sentinel / marker for Recent DOS blank filter (`__worksheet_blank_dos__` → API `last_dos=no_dos`). */
  dosContains: string;
  /** Comma-separated local calendar days `yyyy-MM-dd` (worklist DOS column checkboxes → API `dos_start` min / `dos_end` max). */
  dosDayKeysContains: string | null;
  /** Comma-separated `yyyy-MM-dd` (Created column checkboxes → API `startDate` min / `endDate` max). */
  createdDayKeysContains: string | null;
  /** Comma-separated `yyyy-MM-dd` (Last Activity column checkboxes → `activity_date_start` min / `activity_date_end` max). */
  updatedDayKeysContains: string | null;
  refStatusContains: string | null;
  payerContains: string | null;
  /** Comma-separated PHI directory `eligibility` values (`Verified`, `Unverified`, `No Coverage`, …). */
  eligibilityContains: string | null;
  coordinatorContains: string | null;
  /** Comma-separated PHI directory `claim_status` values. */
  claimStatusContains: string | null;
  /** Substring match on {@link ListViewComponent.getWorksheetVisitTypeDisplay} (Clinic / Telehealth / …). */
  visitTypeContains: string;
  /** Substring match on auth label ({@link ListViewComponent.getAuth} plain text). */
  authStatusContains: string;
  /**
   * Engage referrals API: each entry is a query flag sent as `&{key}=true` (e.g. `has_new_lead`, `has_missed`).
   */
  referralNotificationFilters: string[];
}

export function emptyWorksheetGridClientFilters(): WorksheetGridClientFilters {
  return {
    nameContains: "",
    namePatientKeysContains: null,
    referralsFullNameFilters: null,
    dobContains: "",
    dobDayKeysContains: null,
    queueContains: null,
    dosContains: "",
    dosDayKeysContains: null,
    createdDayKeysContains: null,
    updatedDayKeysContains: null,
    refStatusContains: null,
    payerContains: null,
    eligibilityContains: null,
    coordinatorContains: null,
    claimStatusContains: null,
    visitTypeContains: "",
    authStatusContains: "",
    referralNotificationFilters: [],
  };
}

/** True when no worklist grid text filters are set (DOS committed range is checked separately). */
export function worksheetGridClientFiltersAreEmpty(g: WorksheetGridClientFilters | null | undefined): boolean {
  if (!g) {
    return true;
  }
  const t = (s: string | null | undefined) => String(s ?? "").trim();
  const notif = Array.isArray(g.referralNotificationFilters)
    ? g.referralNotificationFilters.filter((x) => String(x ?? "").trim())
    : [];
  return (
    t(g.nameContains) === "" &&
    t(g.namePatientKeysContains) === "" &&
    t(g.referralsFullNameFilters) === "" &&
    t(g.dobContains) === "" &&
    t(g.dobDayKeysContains) === "" &&
    t(g.queueContains) === "" &&
    t(g.dosContains) === "" &&
    t(g.dosDayKeysContains) === "" &&
    t(g.createdDayKeysContains) === "" &&
    t(g.updatedDayKeysContains) === "" &&
    t(g.refStatusContains) === "" &&
    t(g.payerContains) === "" &&
    t(g.eligibilityContains) === "" &&
    t(g.coordinatorContains) === "" &&
    t(g.claimStatusContains) === "" &&
    t(g.visitTypeContains) === "" &&
    t(g.authStatusContains) === "" &&
    notif.length === 0
  );
}

/** Procedure list page (`/procedure-list`) status tabs. */
export type ProcedureListTabId =
  | "today"
  | "open"
  | "sign_req"
  | "retro_auth"
  | "ready_billing";

export const PROCEDURE_LIST_TAB_DEFS: ReadonlyArray<{
  id: ProcedureListTabId;
  label: string;
}> = [
  { id: "today", label: "Today" },
  { id: "open", label: "Open" },
  { id: "retro_auth", label: "Retro Auth" },
  { id: "sign_req", label: "Sign Req" },
  { id: "ready_billing", label: "Ready for Billing" },
] as const;

/** Map `status-counts` API `filterStatus` label to procedure list tab id. */
export function procedureListTabIdFromApiStatusLabel(
  label: string | null | undefined
): ProcedureListTabId | null {
  const key = (label ?? "").trim().toLowerCase();
  if (!key) {
    return null;
  }
  const tab = PROCEDURE_LIST_TAB_DEFS.find((t) => t.label.toLowerCase() === key);
  return tab?.id ?? null;
}

/** Procedure list tabs that open `/encounters/:id` instead of the procedure room. */
export const PROCEDURE_LIST_ENCOUNTER_PAGE_TAB_IDS: readonly ProcedureListTabId[] = [
  "open",
  "sign_req",
  "retro_auth",
  "ready_billing",
] as const;

export function procedureListTabOpensEncounterPage(
  tabId: ProcedureListTabId | string | null | undefined
): boolean {
  return PROCEDURE_LIST_ENCOUNTER_PAGE_TAB_IDS.includes(
    tabId as ProcedureListTabId
  );
}

/** Procedure list grid: Status column header label for the active status tab. */
export function procedureListStatusColumnLabel(
  tabId: ProcedureListTabId | string | null | undefined
): string {
  switch (tabId) {
    case "today":
      return "Procedure Status";
    case "retro_auth":
      return "Auth Status";
    case "open":
    case "sign_req":
    case "ready_billing":
      return "Encounter Status";
    default:
      return "Status";
  }
}

/** Procedure list filter drawer — procedure status radio values. */
export type ProcedureListFilterStatusId =
  | ""
  | "open"
  | "missing_signatures"
  | "upcoming"
  | "missing_dictations"
  | "ready_billing"
  | "complete";

export type ProcedureListFilterDatePresetId =
  | "all"
  | "today"
  | "last_week"
  | "previous"
  | "custom";

export interface ProcedureListSheetFilters {
  providerId: string;
  statusId: ProcedureListFilterStatusId;
  datePreset: ProcedureListFilterDatePresetId;
  customStart: Date | null;
  customEnd: Date | null;
  /** Account service ids selected in the filter drawer. */
  serviceIds: string[];
  /** Referral source ids/values selected in the filter drawer. */
  referralSourceIds: string[];
}

export const DEFAULT_PROCEDURE_LIST_SHEET_FILTERS: ProcedureListSheetFilters = {
  providerId: "",
  statusId: "",
  datePreset: "all",
  customStart: null,
  customEnd: null,
  serviceIds: [],
  referralSourceIds: [],
};

export const PROCEDURE_LIST_FILTER_STATUS_OPTIONS: ReadonlyArray<{
  id: Exclude<ProcedureListFilterStatusId, "">;
  label: string;
}> = [
  { id: "open", label: "Open" },
  { id: "missing_signatures", label: "Missing Signatures" },
  { id: "upcoming", label: "Upcoming" },
  { id: "missing_dictations", label: "Missing Dictations" },
  { id: "ready_billing", label: "Ready for Billing" },
  { id: "complete", label: "Complete" },
] as const;

export const PROCEDURE_LIST_FILTER_DATE_OPTIONS: ReadonlyArray<{
  id: ProcedureListFilterDatePresetId;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "last_week", label: "Last Week" },
  { id: "previous", label: "Previous" },
  { id: "custom", label: "Custom" },
] as const;
